import { DescriptionOutlined, Download } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import usePickupReports from "hooks/pickups/usePickupReports";
import useAnalytics from "hooks/analytics/useAnalytics";
import { downloadFile } from "utils/files";
import { Pickup } from "types/pickup";
import { useEffect } from "react";
import { initialReportActionCopy } from "utils/initialReportActionCopy";

const accent = "#7A4F01";
const surface = "#FFF9E1";
const border = "#E8D4A0";
const badgeBg = "#FBEBA5";
const idHighlight = "rgba(122, 79, 1, 0.12)";

type InitialReportReadyBannerProps = {
  pickup: Pickup;
};

export default function InitialReportReadyBanner({ pickup }: InitialReportReadyBannerProps) {
  const { getInitialReportData, initialReportData, setDownloadInitialReport, downloadInitialReport } =
    usePickupReports();
  const { createAnalyticsEvent } = useAnalytics();
  const hours = Number(process.env.REACT_APP_HOURS_PUBLISHED_AFTER_WINDOW_LIMIT || 72);

  useEffect(() => {
    if (!downloadInitialReport) return;
    setDownloadInitialReport(false);
    downloadFile(initialReportData, `pickup_${pickup.orderNumber}_initial_report.csv`);
    createAnalyticsEvent("initial_report_downloaded");
  }, [downloadInitialReport, initialReportData, pickup.orderNumber, setDownloadInitialReport, createAnalyticsEvent]);

  return (
    <Box
      sx={{
        position: "relative",
        py: 2,
        px: 2.5,
        borderRadius: 1,
        border: `1px solid ${border}`,
        bgcolor: surface,
        color: accent,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          bgcolor: badgeBg,
          typography: "caption",
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: accent,
          lineHeight: 1.2,
        }}
        component="span"
      >
        ACTION REQUIRED
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <DescriptionOutlined
          sx={{
            color: accent,
            fontSize: 36,
            flexShrink: 0,
            mt: 0.25,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: accent }}>
            Your Initial Report for Pickup{" "}
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: 0.75,
                py: 0.125,
                borderRadius: 0.5,
                bgcolor: idHighlight,
                fontWeight: 700,
              }}
            >
              {pickup.orderNumber}
            </Box>{" "}
            is ready
          </Typography>

          <Typography variant="body2" sx={{ color: accent, mt: 1, opacity: 0.95 }}>
            {pickup.enrollmentV2 ? (
              initialReportActionCopy(hours)
            ) : (
              <>
                Download the report to get the full list of serialised devices. Unlock them within{" "}
                <Box component="span" fontWeight={700}>
                  {hours} hours
                </Box>{" "}
                for optimal processing timing.
              </>
            )}
          </Typography>

          <Button
            type="button"
            variant="outlined"
            size="medium"
            startIcon={<Download sx={{ color: accent }} />}
            onClick={() => getInitialReportData(pickup.uuid)}
            sx={{
              mt: 2,
              textTransform: "none",
              fontWeight: 600,
              color: accent,
              borderColor: border,
              bgcolor: "#fff",
              "&:hover": {
                borderColor: accent,
                bgcolor: "#fff",
              },
            }}
          >
            Download IR
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
